<template>
  <div class="product-mapping-row">
    <div class="product-mapping-row__grid">
      <BittsSelect
        :aria-label="`CRM field for ${row.label}`"
        placeholder="Select your CRM field"
        :allow-clear="true"
        :data-testid="row.testId"
        :loading="loading"
        :disabled="disabled"
        :model-value="modelValue"
        :options="options"
        :error="Boolean(errorMessage)"
        @update:model-value="$emit('update:model-value', $event)"
        @dropdown-visible-change="handleDropdownVisibleChange"
      />
      <FontAwesomeIcon
        :icon="['fak', 'arrow-right']"
        class="product-mapping-row__arrow"
      />
      <BittsSelect
        disabled
        aria-label="Crossbeam field"
        :model-value="row.mdmProperty"
        :options="[{ value: row.mdmProperty, label: row.label }]"
      />
    </div>
    <div
      v-if="errorMessage || noOptionsMessage"
      class="product-mapping-row__grid"
    >
      <p
        v-if="errorMessage"
        class="product-mapping-row__error"
        :data-testid="`${row.testId}-error`"
      >
        <FontAwesomeIcon
          :icon="['fak', 'information-circle']"
          class="product-mapping-row__error-icon"
        />
        {{ errorMessage }}
      </p>
      <p
        v-else
        class="product-mapping-row__no-options"
        :data-testid="`${row.testId}-no-options`"
      >
        {{ noOptionsMessage }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsSelect } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import type { MappingRowConfig } from '../constants';

type SelectOption = { value: string; label: string };

type Props = {
  row: MappingRowConfig;
  modelValue: string | undefined;
  options: SelectOption[];
  disabled: boolean;
  loading: boolean;
  errorMessage?: string;
  noOptionsMessage?: string;
};

defineProps<Props>();

const emit = defineEmits<{
  'update:model-value': [value: string | undefined];
  touched: [];
}>();

/* Closing the picker without choosing is what makes a row dirty, so the blank
   required row can mark itself without an outside-click listener. */
function handleDropdownVisibleChange(isOpen: boolean) {
  if (!isOpen) emit('touched');
}
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.product-mapping-row__grid {
  @apply grid grid-cols-[1fr_24px_1fr] gap-16 items-center;
}

.product-mapping-row__arrow {
  @apply text-neutral-accent justify-self-center;
}

.product-mapping-row__no-options {
  @apply text-neutral-text-weak text-sm mt-8;
}

/* The message sits outside the select's own grid cell, so a row that errors
   keeps the arrow and the Crossbeam field centred against the select. */
.product-mapping-row__error {
  @apply flex items-center text-danger-text-strong text-sm mt-8;
}

.product-mapping-row__error-icon {
  @apply text-danger-accent mr-4 h-12 w-12;
}
</style>
